/**
 * Expandable session card for matching (participants and assignments).
 */
import * as React from 'react';
import { Calendar, Clock, MapPin, BookOpen, Building2, Workflow, Repeat2 } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UnifiedDropZone } from '@/components/common/UnifiedDropZone';
import { LabelBadge } from '@/components/common/LabelBadge';
import { AutoAssignmentModal } from './AutoAssignmentModal';
import {
  setSessionAssignments,
  runSessionAutoAssignment,
} from '@/services/inperson/matching.service';
import type { SessionForMatching } from '@/types/inperson/matching.types';
import type { UserWithProfile } from '@/types/user.types';
import {
  buildMatchingAssignmentPairs,
  buildMatchingAssignmentParticipants,
  canAddMatchingParticipant,
  canRunMatchingAutoAssign,
  canSaveMatchingAssignments,
  deriveMatchingSessionHeader,
  formatMatchingParticipantsSummary,
  getMatchingSessionMaxStudents,
  getMatchingStudentsOverCapacity,
  getTutorMatchedStudents,
  hydrateSessionLocalState,
  mapAutoAssignPairsToMatches,
  type MatchingStudentTutorMatch,
} from '../matching-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link MatchingCollapse}. */
interface MatchingCollapseProps {
  session: SessionForMatching;
  isExpanded: boolean;
  onCountsChange?: (tutorCount: number, studentCount: number) => void;
  onRegisterHandlers?: (
    sessionId: string,
    handlers: {
      handleAddTutor: (user: UserWithProfile) => void;
      handleAddStudent: (user: UserWithProfile) => void;
      handleMatchStudentToTutor: (studentId: string, tutorId: string) => void;
      handleUnmatchStudent: (studentId: string, tutorId: string) => void;
      getSpecificMatches: () => Array<{ studentId: string; tutorId: string }>;
      getTempTutors: () => UserWithProfile[];
      getTempStudents: () => UserWithProfile[];
    },
  ) => void;
  onParticipantsChange?: () => void;
}

/** Expandable session card: participants, matches, auto-assign, and save. */
export const MatchingCollapse: React.FC<MatchingCollapseProps> = ({
  session,
  isExpanded,
  onCountsChange,
  onRegisterHandlers,
  onParticipantsChange,
}) => {
  const queryClient = useQueryClient();

  const [tempTutors, setTempTutors] = React.useState<UserWithProfile[]>([]);
  const [tempStudents, setTempStudents] = React.useState<UserWithProfile[]>([]);
  const [specificMatches, setSpecificMatches] = React.useState<MatchingStudentTutorMatch[]>([]);
  const [showAutoAssignModal, setShowAutoAssignModal] = React.useState(false);

  /** Activity cap first, then session `maxStudents`. */
  const maxStudents = React.useMemo(() => getMatchingSessionMaxStudents(session), [session]);

  /** How many students exceed the cap (shown on the drop zone). */
  const studentsOverCapacity = React.useMemo(
    () => getMatchingStudentsOverCapacity(tempStudents.length, maxStudents),
    [tempStudents.length, maxStudents],
  );

  React.useEffect(() => {
    const hydrated = hydrateSessionLocalState(session);
    if (hydrated.tutors) setTempTutors(hydrated.tutors);
    if (hydrated.students) setTempStudents(hydrated.students);
    if (hydrated.matches) setSpecificMatches(hydrated.matches);
  }, [session.participants, session.studentTutorPairs]);

  /** Persist pairs + participants for this session. */
  const saveMutation = useMutation({
    mutationFn: () => {
      return setSessionAssignments(session.id, {
        pairs: buildMatchingAssignmentPairs(specificMatches, tempStudents, tempTutors),
        participants: buildMatchingAssignmentParticipants(tempTutors, tempStudents),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['matching-pairs'] }),
        queryClient.invalidateQueries({ queryKey: ['assignment-candidates', session.id] }),
      ]);
      toast.success('Affectations enregistrées avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Run auto-assign and replace `specificMatches` with the returned pairs. */
  const autoAssignMutation = useMutation({
    mutationFn: () => {
      return runSessionAutoAssignment(
        session.id,
        buildMatchingAssignmentParticipants(tempTutors, tempStudents),
      );
    },
    onSuccess: async (pairs) => {
      setSpecificMatches(mapAutoAssignPairsToMatches(pairs));

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['matching-pairs'] }),
        queryClient.invalidateQueries({ queryKey: ['assignment-candidates', session.id] }),
      ]);
      toast.success(`Auto-affectation réussie ! ${pairs.length} matchings créés.`);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const { dateLabel, timeLabel, establishmentDisplay, recurrenceLabel } = React.useMemo(
    () => deriveMatchingSessionHeader(session),
    [session],
  );

  /** Add a tutor unless already present or over `maxStudentsPerTutor`. */
  const handleAddTutor = React.useCallback(
    (user: UserWithProfile) => {
      setTempTutors((prev) => {
        if (prev.some((t) => t.id === user.id)) return prev;
        if (!canAddMatchingParticipant(prev.length, session.activity?.maxStudentsPerTutor)) {
          return prev;
        }
        return [...prev, user];
      });
      setTimeout(() => onParticipantsChange?.(), 0);
    },
    [onParticipantsChange, session.activity?.maxStudentsPerTutor],
  );

  /** Add a student unless already present or over the session cap. */
  const handleAddStudent = React.useCallback(
    (user: UserWithProfile) => {
      setTempStudents((prev) => {
        if (prev.some((s) => s.id === user.id)) return prev;
        if (!canAddMatchingParticipant(prev.length, maxStudents)) {
          return prev;
        }
        return [...prev, user];
      });
      setTimeout(() => onParticipantsChange?.(), 0);
    },
    [onParticipantsChange, maxStudents],
  );

  /** Removing a tutor also drops their matches. */
  const handleRemoveTutor = React.useCallback(
    (userId: string) => {
      setTempTutors((prev) => prev.filter((t) => t.id !== userId));
      setSpecificMatches((prev) => prev.filter((m) => m.tutorId !== userId));
      setTimeout(() => onParticipantsChange?.(), 0);
    },
    [onParticipantsChange],
  );

  /** Removing a student also drops their matches. */
  const handleRemoveStudent = React.useCallback(
    (userId: string) => {
      setTempStudents((prev) => prev.filter((s) => s.id !== userId));
      setSpecificMatches((prev) => prev.filter((m) => m.studentId !== userId));
      setTimeout(() => onParticipantsChange?.(), 0);
    },
    [onParticipantsChange],
  );

  /** Ignore duplicate student–tutor pairs. */
  const handleMatchStudentToTutor = React.useCallback((studentId: string, tutorId: string) => {
    setSpecificMatches((prev) => {
      if (prev.some((m) => m.studentId === studentId && m.tutorId === tutorId)) return prev;
      return [...prev, { studentId, tutorId }];
    });
  }, []);

  /** Drop one student–tutor pair without removing the participants. */
  const handleUnmatchStudent = React.useCallback((studentId: string, tutorId: string) => {
    setSpecificMatches((prev) =>
      prev.filter((m) => !(m.studentId === studentId && m.tutorId === tutorId)),
    );
  }, []);

  /** Students currently matched to this tutor (for tutor-chip badges). */
  const getTutorMatchedStudentsForChip = React.useCallback(
    (tutorId: string) => getTutorMatchedStudents(tutorId, specificMatches, tempStudents),
    [specificMatches, tempStudents],
  );

  /** Stable getters so the parent can exclude current participants without capturing an old closure. */
  const getSpecificMatches = React.useCallback(() => specificMatches, [specificMatches]);
  const getTempTutors = React.useCallback(() => tempTutors, [tempTutors]);
  const getTempStudents = React.useCallback(() => tempStudents, [tempStudents]);

  React.useEffect(() => {
    if (onRegisterHandlers) {
      onRegisterHandlers(session.id, {
        handleAddTutor,
        handleAddStudent,
        handleMatchStudentToTutor,
        handleUnmatchStudent,
        getSpecificMatches,
        getTempTutors,
        getTempStudents,
      });
    }
  }, [
    session.id,
    onRegisterHandlers,
    handleAddTutor,
    handleAddStudent,
    handleMatchStudentToTutor,
    handleUnmatchStudent,
    getSpecificMatches,
    getTempTutors,
    getTempStudents,
  ]);

  React.useEffect(() => {
    onCountsChange?.(tempTutors.length, tempStudents.length);
  }, [onCountsChange, tempStudents.length, tempTutors.length]);

  function handleOpenAutoAssign(): void {
    setShowAutoAssignModal(true);
  }

  function handleCloseAutoAssign(): void {
    setShowAutoAssignModal(false);
  }

  function handleConfirmAutoAssign(): void {
    autoAssignMutation.mutate();
    setShowAutoAssignModal(false);
  }

  function handleSaveAssignments(): void {
    saveMutation.mutate();
  }

  return (
    <>
      {isExpanded && (
        <div className="matching-collapse-content">
          <div className="matching-details">
            <div className="flex flex-wrap gap-2 items-center">
              <LabelBadge
                label={dateLabel}
                icon={Calendar}
                variant="outlined"
                color="var(--color-2-nuance-1)"
                size="sm"
                className="min-w-0 max-w-full"
              />
              <LabelBadge
                label={timeLabel}
                icon={Clock}
                variant="outlined"
                color="var(--color-1-nuance-1)"
                size="sm"
                className="min-w-0 max-w-full tabular-nums"
              />
              {establishmentDisplay && (
                <LabelBadge
                  label={establishmentDisplay}
                  icon={Building2}
                  variant="outlined"
                  color="var(--color-1-nuance-1)"
                  size="sm"
                  className="min-w-0 max-w-full"
                />
              )}
              {session.room && (
                <LabelBadge
                  label={session.room.name}
                  icon={MapPin}
                  variant="outlined"
                  color="var(--color-1-nuance-2)"
                  size="sm"
                  className="min-w-0 max-w-full"
                />
              )}
              {session.subjectNames && session.subjectNames.length > 0 && (
                <LabelBadge
                  label={session.subjectNames.join(', ')}
                  icon={BookOpen}
                  variant="outlined"
                  color="var(--color-1-nuance-2)"
                  size="sm"
                  className="min-w-0 max-w-full"
                />
              )}
              {recurrenceLabel && (
                <LabelBadge
                  label={recurrenceLabel}
                  icon={Repeat2}
                  variant="outlined"
                  color="var(--color-3-nuance-1)"
                  size="sm"
                  className="min-w-0 max-w-full"
                />
              )}
            </div>
          </div>

          <UnifiedDropZone
            tutors={tempTutors}
            students={tempStudents}
            maxTutors={session.activity?.maxStudentsPerTutor}
            maxStudents={maxStudents}
            onRemoveTutor={handleRemoveTutor}
            onRemoveStudent={handleRemoveStudent}
            sessionId={session.id}
            specificMatches={specificMatches}
            getTutorMatchedStudents={getTutorMatchedStudentsForChip}
            onUnmatchStudent={handleUnmatchStudent}
            mode="matching"
            studentsOverCapacity={studentsOverCapacity}
          />

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {formatMatchingParticipantsSummary(
                tempTutors.length,
                tempStudents.length,
                specificMatches.length,
              )}
            </p>
            <Button
              variant="secondary"
              size="md"
              icon={Workflow}
              iconPosition="left"
              onClick={handleOpenAutoAssign}
              disabled={
                autoAssignMutation.isPending ||
                saveMutation.isPending ||
                !canRunMatchingAutoAssign(tempTutors.length, tempStudents.length)
              }
            >
              Affectation automatique
            </Button>
            <Button
              preset="form-save"
              loading={saveMutation.isPending}
              onClick={handleSaveAssignments}
              disabled={
                saveMutation.isPending ||
                autoAssignMutation.isPending ||
                !canSaveMatchingAssignments(
                  tempTutors.length,
                  tempStudents.length,
                  specificMatches.length,
                )
              }
            >
              Valider l'affectation
            </Button>
          </div>
        </div>
      )}

      <AutoAssignmentModal
        open={showAutoAssignModal}
        onClose={handleCloseAutoAssign}
        onConfirm={handleConfirmAutoAssign}
        isLoading={autoAssignMutation.isPending}
      />
    </>
  );
};
